"use client";

import { AccentCheck } from "@health/ui";
import { ChevronRight } from "lucide-react";
import { formatFieldLabel, formatFieldValue } from "./tool-status-copy";

type Props = {
  fieldName?: string;
  fieldValue: unknown;
  persisted?: boolean;
  /** When provided, renders a chevron button that opens the journey panel. */
  onOpenPanel?: () => void;
};

export function TraceCard({ fieldName, fieldValue, persisted, onOpenPanel }: Props) {
  const label = fieldName ? formatFieldLabel(fieldName) : null;
  const displayValue = formatFieldValue(fieldValue);
  const hasPanel = !!onOpenPanel;

  return (
    <div
      className={`flex flex-col gap-1.5 text-sm pt-2 ${
        persisted ? "opacity-50" : "opacity-70 animate-fade-in transition-opacity duration-300"
      }`}
      aria-live="polite"
    >
      {/* Header: checkmark + "Got it" */}
      <div className="flex items-center gap-1.5">
        <AccentCheck />
        <span className={hasPanel ? "text-accent" : "text-text-secondary"}>Got it</span>
      </div>

      {/* Content: field label and value + optional chevron */}
      {(label || displayValue) && (
        <div className="flex items-center gap-2">
          <div
            className={`flex flex-1 flex-col gap-0.5 pl-[22px] ${
              hasPanel ? "ml-0.5 border-l-2 border-accent/30 pl-3" : ""
            }`}
          >
            {label && <span className="text-xs text-text-secondary/70">{label}:</span>}
            {displayValue && (
              <span
                className={
                  hasPanel
                    ? "text-sm font-medium text-text-primary"
                    : "text-xs text-text-secondary/70"
                }
              >
                {displayValue}
              </span>
            )}
          </div>
          {hasPanel && (
            <button
              type="button"
              onClick={onOpenPanel}
              aria-label="View your journey"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-accent/60 transition-colors duration-200 hover:bg-accent/5 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
